import { useQuery } from '@tanstack/react-query';
import {
  doc,
  DocumentData,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { db } from '../Firebase';
import { useSlackConnectedAccounts } from '../hooks/useSlackConnectedAccounts';
import { listConnectorInstallations } from '../services/ConnectorsService';
import { getIntegrations } from '../services/IntegrationsService';
import {
  LAUNCH_CHECKLIST_STEP_CONFIGS,
  LAUNCH_CHECKLIST_STEP_IDS,
  LaunchChecklistStepConfig,
  LaunchChecklistStepId,
} from '../utils/launchChecklistSteps';
import { useAnalytics } from './AnalyticsContext';
import { AuthContext } from './AuthContext';
import { useDashboardContext } from './DashboardContext';
import { UserContext } from './UserContext';

type ChecklistStatus = 'active' | 'completed';
type ChecklistCompletionSource = 'manual' | 'system';
type ChecklistCompletedReason = 'manual' | 'all_steps_resolved';

interface LaunchChecklistStepCompletion {
  status: 'completed';
  source: ChecklistCompletionSource;
  completedAt?: unknown;
  completedBy?: string;
  updatedAt?: unknown;
  updatedBy?: string;
}

interface LaunchChecklistState {
  status?: ChecklistStatus;
  stepCompletions?: Partial<
    Record<LaunchChecklistStepId, LaunchChecklistStepCompletion>
  >;
  completedAt?: unknown;
  completedBy?: string;
  completedReason?: ChecklistCompletedReason;
  updatedAt?: unknown;
  updatedBy?: string;
}

export interface LaunchChecklistStep extends LaunchChecklistStepConfig {
  primaryHref?: string;
  completed: boolean;
  current: boolean;
  completionSource?: ChecklistCompletionSource;
}

interface AccountActivationChecklistContextValue {
  steps: LaunchChecklistStep[];
  completedStepsCount: number;
  totalStepsCount: number;
  progressPercentage: number;
  status: ChecklistStatus;
  shouldRender: boolean;
  markStepComplete: (stepId: LaunchChecklistStepId) => Promise<void>;
  markChecklistComplete: () => Promise<void>;
}

const EMPTY_CONTEXT: AccountActivationChecklistContextValue = {
  steps: [],
  completedStepsCount: 0,
  totalStepsCount: 0,
  progressPercentage: 0,
  status: 'active',
  shouldRender: false,
  markStepComplete: async () => {},
  markChecklistComplete: async () => {},
};

const AccountActivationChecklistContext =
  createContext<AccountActivationChecklistContextValue>(EMPTY_CONTEXT);

function checklistDocRef(accountId: string) {
  return doc(db, 'accounts', accountId, 'activationChecklists', 'launch');
}

function normalizeState(data?: DocumentData): LaunchChecklistState {
  if (!data) return {};

  return data as LaunchChecklistState;
}

export function AccountActivationChecklistProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { firebaseUser } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const dashboard = useDashboardContext();
  const { track } = useAnalytics();
  const [checklistState, setChecklistState] = useState<LaunchChecklistState>();

  const accountId = selectedAccount?.id;
  const accountSlug = selectedAccount?.data().slug;
  const checklistLoaded = !accountId || checklistState !== undefined;

  // Only read the account's collections once the checklist doc says there is
  // still something to complete. DashboardContext opens a Firestore listener
  // the first time a value is read, and this provider wraps every workspace
  // route, so reading unconditionally subscribed six collections on every
  // screen to power a sidebar button that a completed account never sees.
  const checklistActive =
    !!accountId &&
    checklistLoaded &&
    (checklistState?.status ?? 'active') !== 'completed';
  const accountBrand = checklistActive ? dashboard.accountBrand : undefined;
  const adsAccounts = checklistActive ? dashboard.adsAccounts : undefined;
  const domains = checklistActive ? dashboard.domains : undefined;
  const dynamicPages = checklistActive ? dashboard.dynamicPages : undefined;
  const quizzes = checklistActive ? dashboard.quizzes : undefined;
  const teamMembers = checklistActive ? dashboard.teamMembers : undefined;

  const { data: integrations } = useQuery({
    queryKey: ['launch-checklist-integrations', accountId],
    queryFn: () => getIntegrations(accountId!),
    enabled: !!accountId,
    staleTime: 30_000,
  });

  const { data: connectorInstallations } = useQuery({
    queryKey: ['launch-checklist-connectors', accountId],
    queryFn: () => listConnectorInstallations(accountId!),
    enabled: !!accountId,
    staleTime: 30_000,
  });

  const { data: slackConnectedAccounts } = useSlackConnectedAccounts(accountId);

  useEffect(() => {
    setChecklistState(undefined);

    if (!accountId) {
      return;
    }

    const unsubscribe = onSnapshot(checklistDocRef(accountId), (snapshot) => {
      setChecklistState(normalizeState(snapshot.data()));
    });

    return () => {
      unsubscribe();
    };
  }, [accountId]);

  const derivedCompletionByStep = useMemo<
    Record<LaunchChecklistStepId, boolean>
  >(() => {
    const accountData = selectedAccount?.data();

    return {
      teammate_invited: (teamMembers?.length ?? 0) > 1,
      brand_added: !!accountBrand,
      custom_domain_connected:
        domains?.some((domain) => domain.data().type === 'custom') ?? false,
      ad_account_connected:
        !!adsAccounts?.length ||
        !!accountData?.googleAdsAccountConnected ||
        !!accountData?.facebookAdsAccountConnected ||
        !!accountData?.tiktokAdsAccountConnected,
      lead_routing_configured:
        integrations?.some((integration) => {
          return (
            integration.status === 'active' &&
            ['hubspot', 'webhook', 'zapier'].includes(integration.provider) &&
            ['new_lead', 'new_partial_lead'].includes(integration.type)
          );
        }) ?? false,
      slack_alerts_enabled:
        slackConnectedAccounts?.some((connectedAccount) => {
          return connectedAccount.status === 'active';
        }) ?? false,
      agent_tools_connected: !!connectorInstallations?.length,
      landing_page_published:
        (dynamicPages?.some((dynamicPage) => {
          return dynamicPage.data().isLive === true;
        }) ??
          false) ||
        (quizzes?.some((quiz) => {
          const data = quiz.data();
          return 'isLive' in data && data.isLive === true;
        }) ??
          false),
    };
  }, [
    accountBrand,
    adsAccounts,
    connectorInstallations,
    domains,
    dynamicPages,
    integrations,
    quizzes,
    selectedAccount,
    slackConnectedAccounts,
    teamMembers,
  ]);

  const stepCompletions = useMemo(() => {
    return checklistState?.stepCompletions ?? {};
  }, [checklistState?.stepCompletions]);
  const status = checklistState?.status ?? 'active';

  const steps = useMemo<LaunchChecklistStep[]>(() => {
    const firstIncompleteStepId = LAUNCH_CHECKLIST_STEP_CONFIGS.find((step) => {
      return !stepCompletions[step.id] && !derivedCompletionByStep[step.id];
    })?.id;

    return LAUNCH_CHECKLIST_STEP_CONFIGS.map((step) => {
      const persistedCompletion = stepCompletions[step.id];
      const completed =
        !!persistedCompletion || derivedCompletionByStep[step.id];

      return {
        ...step,
        primaryHref: accountSlug ? step.route(accountSlug) : undefined,
        completed,
        current: step.id === firstIncompleteStepId,
        completionSource:
          persistedCompletion?.source ||
          (derivedCompletionByStep[step.id] ? 'system' : undefined),
      };
    });
  }, [accountSlug, derivedCompletionByStep, stepCompletions]);

  const completedStepsCount = useMemo(() => {
    return steps.filter((step) => step.completed).length;
  }, [steps]);
  const totalStepsCount = steps.length;
  const progressPercentage = totalStepsCount
    ? (completedStepsCount / totalStepsCount) * 100
    : 0;
  const shouldRender =
    !!selectedAccount &&
    checklistLoaded &&
    status !== 'completed' &&
    totalStepsCount > 0;

  const writeSystemStepCompletions = useCallback(
    async (stepIds: LaunchChecklistStepId[]) => {
      if (!accountId || !stepIds.length) return;

      const now = serverTimestamp();
      const completions = stepIds.reduce<
        Partial<Record<LaunchChecklistStepId, LaunchChecklistStepCompletion>>
      >((acc, stepId) => {
        acc[stepId] = {
          status: 'completed',
          source: 'system',
          completedAt: now,
          updatedAt: now,
        };
        return acc;
      }, {});

      await setDoc(
        checklistDocRef(accountId),
        {
          status: 'active',
          stepCompletions: completions,
          updatedAt: now,
        },
        { merge: true },
      );
    },
    [accountId],
  );

  useEffect(() => {
    if (!accountId || !checklistLoaded || status === 'completed') {
      return;
    }

    const unsavedSystemStepIds = LAUNCH_CHECKLIST_STEP_IDS.filter((stepId) => {
      return derivedCompletionByStep[stepId] && !stepCompletions[stepId];
    });

    if (!unsavedSystemStepIds.length) {
      return;
    }

    writeSystemStepCompletions(unsavedSystemStepIds).catch((error) => {
      console.error('Failed to save launch checklist system completion', error);
    });
  }, [
    accountId,
    checklistLoaded,
    derivedCompletionByStep,
    status,
    stepCompletions,
    writeSystemStepCompletions,
  ]);

  useEffect(() => {
    if (
      !accountId ||
      !checklistLoaded ||
      status === 'completed' ||
      completedStepsCount < steps.length
    ) {
      return;
    }

    const now = serverTimestamp();
    setDoc(
      checklistDocRef(accountId),
      {
        status: 'completed',
        completedAt: now,
        completedReason: 'all_steps_resolved',
        updatedAt: now,
      },
      { merge: true },
    ).catch((error) => {
      console.error('Failed to complete launch checklist', error);
    });
  }, [accountId, checklistLoaded, completedStepsCount, status, steps.length]);

  const markStepComplete = useCallback(
    async (stepId: LaunchChecklistStepId) => {
      if (!accountId || !firebaseUser) return;

      const now = serverTimestamp();
      const nextStepCompletions = {
        ...stepCompletions,
        [stepId]: {
          status: 'completed',
          source: 'manual',
          completedAt: now,
          completedBy: firebaseUser.uid,
          updatedAt: now,
          updatedBy: firebaseUser.uid,
        },
      };
      const allStepsComplete = LAUNCH_CHECKLIST_STEP_IDS.every((id) => {
        return !!nextStepCompletions[id] || derivedCompletionByStep[id];
      });

      await setDoc(
        checklistDocRef(accountId),
        {
          status: allStepsComplete ? 'completed' : 'active',
          stepCompletions: {
            [stepId]: nextStepCompletions[stepId],
          },
          ...(allStepsComplete
            ? {
                completedAt: now,
                completedBy: firebaseUser.uid,
                completedReason: 'all_steps_resolved',
              }
            : {}),
          updatedAt: now,
          updatedBy: firebaseUser.uid,
        },
        { merge: true },
      );

      track('launch_checklist_step_marked_complete', {
        step_id: stepId,
        account_id: accountId,
        completed_checklist: allStepsComplete,
      });
    },
    [accountId, derivedCompletionByStep, firebaseUser, stepCompletions, track],
  );

  const markChecklistComplete = useCallback(async () => {
    if (!accountId || !firebaseUser) return;

    const now = serverTimestamp();
    await setDoc(
      checklistDocRef(accountId),
      {
        status: 'completed',
        completedAt: now,
        completedBy: firebaseUser.uid,
        completedReason: 'manual',
        updatedAt: now,
        updatedBy: firebaseUser.uid,
      },
      { merge: true },
    );

    track('launch_checklist_completed', {
      account_id: accountId,
      completed_reason: 'manual',
    });
  }, [accountId, firebaseUser, track]);

  const value = useMemo<AccountActivationChecklistContextValue>(() => {
    return {
      steps,
      completedStepsCount,
      totalStepsCount,
      progressPercentage,
      status,
      shouldRender,
      markStepComplete,
      markChecklistComplete,
    };
  }, [
    completedStepsCount,
    markChecklistComplete,
    markStepComplete,
    progressPercentage,
    shouldRender,
    status,
    steps,
    totalStepsCount,
  ]);

  return (
    <AccountActivationChecklistContext.Provider value={value}>
      {children}
    </AccountActivationChecklistContext.Provider>
  );
}

export function useAccountActivationChecklist() {
  return useContext(AccountActivationChecklistContext);
}
